import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminHeader, ReminderBanner } from "@/components/admin/AdminHeader";
import { UploadTab } from "@/components/admin/UploadTab";
import { ChartsTab } from "@/components/admin/ChartsTab";
import { CategoriesTab } from "@/components/admin/CategoriesTab";
import { ResultsTab } from "@/components/admin/ResultsTab";
import { TextsTab } from "@/components/admin/TextsTab";
import { TelegramTab } from "@/components/admin/TelegramTab";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("upload");
  const [categories, setCategories] = useState({});
  const [charts, setCharts] = useState([]);
  const [telegramConfigured, setTelegramConfigured] = useState(false);
  const [telegramInfo, setTelegramInfo] = useState(null);

  const [variant, setVariant] = useState("kl");
  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  const [newCatName, setNewCatName] = useState({});
  const [renameValues, setRenameValues] = useState({});
  const [blastLimit, setBlastLimit] = useState(50);
  const [blasting, setBlasting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [rerenderingAll, setRerenderingAll] = useState(false);
  const [sched, setSched] = useState({ enabled: false, time: "08:00", last_blast: null, last_sent: 0, digest_enabled: false, digest_time: "09:30", weekly_enabled: true, weekly_time: "21:30" });

  const [ocrFiles, setOcrFiles] = useState([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState("");
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrTexts, setOcrTexts] = useState([]);
  const [pubVariant, setPubVariant] = useState("dr");
  const [pubCategoryId, setPubCategoryId] = useState("auto");
  const [publishing, setPublishing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rerendering, setRerendering] = useState(false);
  const [editorZoom, setEditorZoom] = useState("text-xs");

  const [resCategoryId, setResCategoryId] = useState("");
  const [resResult, setResResult] = useState("");
  const [resDate, setResDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [resTime, setResTime] = useState("11AM");
  const [resLoading, setResLoading] = useState(false);
  const [resReport, setResReport] = useState(null);
  const [resJob, setResJob] = useState(null);
  const [resHistory, setResHistory] = useState([]);
  const [reminders, setReminders] = useState([]);

  const loadCategories = () => api.get("/categories").then((res) => setCategories(res.data)).catch(() => {});
  const loadCharts = () => api.get("/charts").then((res) => setCharts(res.data)).catch(() => {});
  const loadTexts = () => api.get("/admin/texts").then((res) => setOcrTexts(res.data)).catch(() => {});
  const loadResults = () => api.get("/admin/results").then((res) => setResHistory(res.data)).catch(() => {});
  const loadReminders = () => api.get("/admin/reminders").then((res) => setReminders(res.data.pending || [])).catch(() => {});

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      toast.info("Session முடிந்தது — மீண்டும் login செய்யுங்கள். உங்கள் வேலை எதுவும் தொலையாது.");
    };
    window.addEventListener("admin-session-expired", onExpired);
    return () => window.removeEventListener("admin-session-expired", onExpired);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("chartshub_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem("chartshub_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    loadCategories();
    loadCharts();
    loadTexts();
    loadResults();
    loadReminders();
    const rt = setInterval(loadReminders, 60000);
    api.get("/admin/telegram/status").then((res) => { setTelegramConfigured(res.data.configured); setTelegramInfo(res.data); }).catch(() => {});
    api.get("/admin/telegram/schedule").then((res) => setSched(res.data)).catch(() => {});
    return () => clearInterval(rt);
  }, [user]);

  useEffect(() => {
    const first = categories[variant]?.[0];
    setCategoryId(first ? first.id : "");
  }, [variant, categories]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.length || !categoryId) {
      toast.error("Pick images and a category");
      return;
    }
    setUploading(true);
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadProgress(`${i + 1}/${files.length}`);
      try {
        const form = new FormData();
        form.append("file", f);
        form.append("variant", variant);
        form.append("category_id", categoryId);
        const autoTitle = f.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
        form.append("title", files.length === 1 && title ? title : autoTitle);
        await api.post("/admin/charts", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch {
        failed++;
      }
    }
    setUploading(false);
    setUploadProgress("");
    if (ok) toast.success(`Uploaded ${ok} chart${ok > 1 ? "s" : ""}`);
    if (failed) toast.error(`${failed} upload${failed > 1 ? "s" : ""} failed`);
    setFiles([]);
    setTitle("");
    loadCharts();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this chart?")) return;
    try {
      await api.delete(`/admin/charts/${id}`);
      toast.success("Chart deleted");
      loadCharts();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleEditChart = async (chart) => {
    try {
      const { data } = await api.get(`/admin/texts/${chart.source_text_id}`);
      setEditing({ chart_id: chart.id, text_id: chart.source_text_id, title: chart.title, text: data.text });
    } catch {
      toast.error("Failed to load chart text");
    }
  };

  const handleRerender = async () => {
    if (!editing) return;
    setRerendering(true);
    try {
      await api.patch(`/admin/texts/${editing.text_id}`, { name: editing.title, text: editing.text });
      await api.post(`/admin/charts/${editing.chart_id}/rerender`, { title: editing.title });
      toast.success("Chart re-rendered");
      setEditing(null);
      loadCharts();
      loadTexts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-render failed");
    } finally {
      setRerendering(false);
    }
  };


  const handleAddCategory = async (v) => {
    const name = (newCatName[v] || "").trim();
    if (!name) return;
    try {
      await api.post("/admin/categories", { variant: v, name });
      toast.success("Category added");
      setNewCatName({ ...newCatName, [v]: "" });
      loadCategories();
    } catch {
      toast.error("Failed to add category");
    }
  };

  const handleRename = async (id) => {
    const name = (renameValues[id] || "").trim();
    if (!name) return;
    try {
      await api.patch(`/admin/categories/${id}`, { name });
      toast.success("Category renamed");
      setRenameValues({ ...renameValues, [id]: "" });
      loadCategories();
      loadCharts();
    } catch {
      toast.error("Rename failed");
    }
  };

  const handleExtract = async (e) => {
    e.preventDefault();
    if (!ocrFiles.length) {
      toast.error("Pick images");
      return;
    }
    const publishAuto = pubCategoryId === "auto";
    const publishManual = Boolean(pubCategoryId) && !publishAuto;
    setOcrLoading(true);
    let ok = 0;
    let published = 0;
    let failed = 0;
    for (let i = 0; i < ocrFiles.length; i++) {
      setOcrProgress(`${i + 1}/${ocrFiles.length}`);
      try {
        const form = new FormData();
        form.append("file", ocrFiles[i]);
        const { data } = await api.post("/admin/extract-text", form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
        setOcrResult(data);
        const target = publishAuto ? data.detected : publishManual ? { variant: pubVariant, category_id: pubCategoryId } : null;
        if (target) {
          const lines = data.text.split("\n");
          let chartTitle = lines[0].trim() || data.name;
          const monthMatch = data.text.match(
            /(JAN(?:UARY)?|FEB(?:RUARY)?|MAR(?:CH)?|APR(?:IL)?|MAY|JUN(?:E)?|JUL(?:Y)?|AUG(?:UST)?|SEP(?:TEMBER)?|OCT(?:OBER)?|NOV(?:EMBER)?|DEC(?:EMBER)?)\s*20\d\d/i
          );
          if (monthMatch && !chartTitle.toUpperCase().includes(monthMatch[0].toUpperCase())) {
            chartTitle = `${chartTitle} — ${monthMatch[0].toUpperCase()}`;
          }
          await api.post(`/admin/texts/${data.id}/render`, {
            variant: target.variant,
            category_id: target.category_id,
            title: chartTitle,
          });
          published++;
        }
        ok++;
      } catch {
        failed++;
      }
    }
    setOcrLoading(false);
    setOcrProgress("");
    if (ok) {
      toast.success(
        published
          ? `Converted ${ok} · published ${published} chart${published > 1 ? "s" : ""}`
          : `Extracted & saved ${ok} text${ok > 1 ? "s" : ""}`
      );
    }
    if (failed) toast.error(`${failed} image${failed > 1 ? "s" : ""} failed`);
    if (publishAuto && published < ok) toast.error(`${ok - published} heading detect ஆகல — category-ஐ manual-ஆ choose பண்ணுங்க`);
    setOcrFiles([]);
    loadTexts();
    loadCharts();
  };

  const handleUpdateText = async () => {
    if (!ocrResult) return;
    try {
      await api.patch(`/admin/texts/${ocrResult.id}`, { name: ocrResult.name, text: ocrResult.text });
      toast.success("Saved");
      loadTexts();
    } catch {
      toast.error("Save failed");
    }
  };

  const handleViewText = async (id) => {
    try {
      const { data } = await api.get(`/admin/texts/${id}`);
      setOcrResult(data);
    } catch {
      toast.error("Load failed");
    }
  };

  const handleDeleteText = async (id) => {
    if (!window.confirm("Delete this extracted text?")) return;
    try {
      await api.delete(`/admin/texts/${id}`);
      toast.success("Deleted");
      if (ocrResult?.id === id) setOcrResult(null);
      loadTexts();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handlePublish = async () => {
    if (!ocrResult) return;
    let target = null;
    if (pubCategoryId === "auto") {
      target = ocrResult.detected;
      if (!target) {
        toast.error("Auto-detect failed — pick category manually");
        return;
      }
    } else if (pubCategoryId) {
      target = { variant: pubVariant, category_id: pubCategoryId };
    }
    if (!target) {
      toast.error("Pick variant and category");
      return;
    }
    setPublishing(true);
    try {
      await api.post(`/admin/texts/${ocrResult.id}/render`, {
        variant: target.variant,
        category_id: target.category_id,
        title: ocrResult.text.split("\n")[0].trim() || ocrResult.name,
      });
      toast.success(`Rendered & published to ${target.category_name || "gallery"}`);
      loadCharts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const handleResultSubmit = async (e) => {
    e.preventDefault();
    if (!resCategoryId || !resResult.trim()) {
      toast.error("Pick draw & enter result");
      return;
    }
    // date double-check: most wrong-date entries happen when yesterday's results
    // are keyed in with today's default date — confirm before accepting
    const todayStr = new Date().toLocaleDateString("en-CA");
    const yesterdayStr = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
    if (resDate === todayStr && !resHistory.some((r) => r.date === yesterdayStr)) {
      if (
        !window.confirm(
          `⚠️ நேற்று (${yesterdayStr}) எந்த entry-ம் இல்லை.\n\nஇது நேற்றைய result-ஆனால்: Cancel அழுத்தி Date-ஐ ${yesterdayStr}-க்கு மாற்றுங்கள்.\nஇது இன்றைய result என்றால்: OK அழுத்துங்கள்.`
        )
      ) {
        return;
      }
    }
    setResLoading(true);
    setResReport(null);
    setResJob(null);
    const cat = allCats.find((c) => c.id === resCategoryId);
    const payload = {
      category_id: resCategoryId,
      result: resResult.trim(),
      date: resDate,
      time: cat?.variantKey === "golden_raja" ? resTime : undefined,
    };
    try {
      let data;
      try {
        ({ data } = await api.post("/admin/results", payload));
      } catch (err) {
        if (err.response?.status !== 409) throw err;
        if (!window.confirm(`⚠️ ${err.response.data.detail}\n\nஇதே draw + time-க்கு ஏற்கனவே entry உள்ளது. மீண்டும் submit செய்யவா? (Pending counts இருமுறை கூடும்!)`)) {
          return;
        }
        ({ data } = await api.post("/admin/results", { ...payload, force: true }));
      }
      toast.success("Result accepted — charts பின்னணியில் update ஆகின்றன");
      loadResults();
      let st = null;
      const t0 = Date.now();
      while (Date.now() - t0 < 300000) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          ({ data: st } = await api.get(`/admin/results/${data.result_id}/status`));
          setResJob({ done: st.done || 0, total: st.total || 0 });
          if (st.status && st.status !== "processing") break;
        } catch {
          /* transient — keep polling */
        }
      }
      if (st?.status === "done") {
        setResReport(st.report || []);
        toast.success(
          `${st.updated_count}/${st.total} charts updated` +
            (st.telegram_sent != null ? ` · Telegram: ${st.telegram_sent} charts அனுப்பப்பட்டன` : "")
        );
        setResResult("");
        loadCharts();
      } else if (st?.status === "failed") {
        toast.error(`Update failed: ${st.error || "unknown"}`);
      } else {
        toast.info("Update இன்னும் நடக்கிறது — சிறிது நேரத்தில் history-இல் பார்க்கவும்");
      }
      loadResults();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Result update failed");
    } finally {
      setResLoading(false);
      setResJob(null);
    }
  };

  const handleUndoResult = async (id) => {
    if (!window.confirm("Undo this result? எல்லா charts-ம் பழைய நிலைக்கு திரும்பும்.")) return;
    try {
      await api.post(`/admin/results/${id}/undo`);
      toast.info("Undo நடக்கிறது — charts பழைய நிலைக்கு திரும்புகின்றன…");
      const t0 = Date.now();
      while (Date.now() - t0 < 300000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: hist } = await api.get("/admin/results");
          setResHistory(hist);
          const row = hist.find((r) => r.id === id);
          if (!row) {
            toast.success("Undone — charts பழைய நிலைக்கு திரும்பின");
            loadCharts();
            return;
          }
          if (row.status === "failed") {
            toast.error(`Undo failed: ${row.error || "unknown"}`);
            return;
          }
        } catch {
          /* transient — keep polling */
        }
      }
      toast.info("Undo இன்னும் நடக்கிறது — சிறிது நேரத்தில் பார்க்கவும்");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Undo failed");
    }
  };

  const handleEditResult = async (id, value, date) => {
    const payload = {};
    const cleaned = (value || "").replace(/\D/g, "");
    if (cleaned) payload.result = cleaned;
    if (date) payload.date = date;
    if (!payload.result && !payload.date) return;
    try {
      await api.post(`/admin/results/${id}/edit`, payload);
      toast.info("Edit நடக்கிறது — சரியான விவரத்துடன் charts update ஆகின்றன…");
      const t0 = Date.now();
      while (Date.now() - t0 < 300000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: hist } = await api.get("/admin/results");
          setResHistory(hist);
          const row = hist.find((r) => r.id === id);
          if (row && row.status === "done") {
            toast.success(`Entry திருத்தப்பட்டது → ${row.result} · ${row.date} (Telegram அனுப்பப்படவில்லை)`);
            loadCharts();
            return;
          }
          if (row && row.status === "failed") {
            toast.error(`Edit failed: ${row.error || "unknown"}`);
            return;
          }
        } catch {
          /* transient — keep polling */
        }
      }
      toast.info("Edit இன்னும் நடக்கிறது — சிறிது நேரத்தில் history-இல் பார்க்கவும்");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Edit failed");
    }
  };

  const pollRenderJob = async (label) => {
    const t0 = Date.now();
    while (Date.now() - t0 < 300000) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await api.get("/admin/charts/rerender-all");
        if (!data.running) {
          toast.success(`${label} முடிந்தது — ${data.done}/${data.total} charts` + (data.failed ? ` · ${data.failed} FAILED` : ""));
          loadCharts();
          return;
        }
      } catch {
        /* keep polling */
      }
    }
    toast.error("Job நீள்கிறது — சற்று நேரம் கழித்து refresh பண்ணுங்க");
    loadCharts();
  };

  const handleRestoreLibrary = async () => {
    if (!window.confirm("Restore full chart library? ஏற்கனவே உள்ள charts duplicate ஆகாது — missing charts மட்டும் சேரும்.")) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/library/restore");
      if (data.charts_queued) {
        toast.success(`Restore தொடங்கியது — ${data.charts_queued} charts queue-இல் (${data.charts_skipped_existing} ஏற்கனவே உள்ளன)`);
        await pollRenderJob("Restore");
      } else {
        toast.success(`எல்லா ${data.pack_total} charts-ம் ஏற்கனவே உள்ளன`);
      }
      api.get("/summary").catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const handleRerenderAll = async () => {
    if (!window.confirm("எல்லா charts-ஐயும் latest style-இல் மீண்டும் render செய்யவா? (1-2 நிமிடம் ஆகும்)")) return;
    setRerenderingAll(true);
    try {
      const { data } = await api.post("/admin/charts/rerender-all");
      if (data.started) {
        toast.success(`Re-render தொடங்கியது — ${data.total} charts`);
        await pollRenderJob("Re-render");
      } else {
        toast.error("ஏற்கனவே ஒரு render job ஓடுகிறது — முடிய காத்திருங்கள்");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-render failed");
    } finally {
      setRerenderingAll(false);
    }
  };

  const handleBlast = async () => {
    setBlasting(true);
    try {
      await api.post("/admin/telegram/blast", { limit: Number(blastLimit) });
      toast.success("Blast தொடங்கியது — charts சில நிமிடங்களில் channel-க்கு போகும்");
      setTimeout(() => setBlasting(false), 15000);
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blast failed");
    }
    setBlasting(false);
  };

  const handleSaveSchedule = async () => {
    try {
      await api.post("/admin/telegram/schedule", {
        enabled: sched.enabled,
        time: sched.time,
        digest_enabled: !!sched.digest_enabled,
        digest_time: sched.digest_time || "09:30",
        weekly_enabled: sched.weekly_enabled !== false,
        weekly_time: sched.weekly_time || "21:30",
      });
      toast.success(
        `Auto-blast ${sched.enabled ? `ON — daily ${sched.time} IST` : "OFF"}` +
          (sched.digest_enabled ? ` · Digest ON — daily ${sched.digest_time || "09:30"} IST` : "") +
          (sched.weekly_enabled !== false ? ` · Weekly card ON — ஞாயிறு ${sched.weekly_time || "21:30"} IST` : "")
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const [weeklyCardUrl, setWeeklyCardUrl] = useState("");
  const handleWeeklyPreview = async () => {
    try {
      const { data } = await api.post("/admin/telegram/weekly-card", { send: false });
      setWeeklyCardUrl(data.url);
      toast.success(`Weekly card ready — ${data.week}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Weekly card failed");
    }
  };
  const handleWeeklySend = async () => {
    try {
      const { data } = await api.post("/admin/telegram/weekly-card", { send: true });
      setWeeklyCardUrl(data.url);
      toast.success(data.sent ? `Weekly card channel-க்கு அனுப்பப்பட்டது — ${data.week}` : "Card ready, ஆனால் Telegram அனுப்ப முடியவில்லை");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };

  const [statusCardUrl, setStatusCardUrl] = useState("");
  const [statusDate, setStatusDate] = useState(() => {
    const ist = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
    return ist.toISOString().slice(0, 10);
  });
  const handleStatusPreview = async () => {
    try {
      const { data } = await api.post("/admin/telegram/status-card", { send: false, date: statusDate });
      setStatusCardUrl(data.url);
      toast.success(`Status card ready — ${data.date}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status card failed");
    }
  };
  const handleStatusSend = async () => {
    try {
      const { data } = await api.post("/admin/telegram/status-card", { send: true, date: statusDate });
      setStatusCardUrl(data.url);
      toast.success(data.sent ? `Status card channel-க்கு அனுப்பப்பட்டது — ${data.date}` : "Card ready, ஆனால் Telegram அனுப்ப முடியவில்லை");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };
  const handleAlertTest = async () => {
    try {
      const { data } = await api.post("/admin/telegram/alert-test", { label: "Dear 1PM" });
      toast.success(data.sent ? "Test alert channel-க்கு அனுப்பப்பட்டது" : "Telegram அனுப்ப முடியவில்லை");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Alert failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("chartshub_token");
    api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (checking) return <div className="min-h-screen bg-[#050505]" />;
  if (!user) return <AdminLogin onLogin={setUser} />;

  const allCats = Object.entries(categories).flatMap(([v, cats]) => cats.map((c) => ({ ...c, variantKey: v })));
  const selectedResCat = allCats.find((c) => c.id === resCategoryId);
  const isGrRes = selectedResCat?.variantKey === "golden_raja";
  const digitHint = selectedResCat
    ? selectedResCat.variantKey === "kl"
      ? "6 digits"
      : isGrRes
        ? "4-5 digits"
        : "5 digits"
    : "";

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-[#FDFBF7]">
      <AdminHeader tab={tab} setTab={setTab} chartsCount={charts.length} onLogout={logout} />
      <ReminderBanner reminders={reminders} onEnter={() => setTab("results")} />

      <div className="mx-auto max-w-6xl px-6 py-10">
        {tab === "upload" && (
          <UploadTab
            categories={categories}
            variant={variant}
            setVariant={setVariant}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            title={title}
            setTitle={setTitle}
            files={files}
            setFiles={setFiles}
            uploading={uploading}
            uploadProgress={uploadProgress}
            onSubmit={handleUpload}
          />
        )}

        {tab === "charts" && (
          <ChartsTab
            charts={charts}
            restoring={restoring}
            onRestore={handleRestoreLibrary}
            rerenderingAll={rerenderingAll}
            onRerenderAll={handleRerenderAll}
            editing={editing}
            setEditing={setEditing}
            rerendering={rerendering}
            onRerender={handleRerender}
            onEdit={handleEditChart}
            onDelete={handleDelete}
          />
        )}

        {tab === "categories" && (
          <CategoriesTab
            categories={categories}
            newCatName={newCatName}
            setNewCatName={setNewCatName}
            renameValues={renameValues}
            setRenameValues={setRenameValues}
            onAdd={handleAddCategory}
            onRename={handleRename}
          />
        )}

        {tab === "results" && (
          <ResultsTab
            allCats={allCats}
            resCategoryId={resCategoryId}
            setResCategoryId={setResCategoryId}
            resResult={resResult}
            setResResult={setResResult}
            resDate={resDate}
            setResDate={setResDate}
            resTime={resTime}
            setResTime={setResTime}
            resLoading={resLoading}
            resReport={resReport}
            resJob={resJob}
            resHistory={resHistory}
            isGrRes={isGrRes}
            digitHint={digitHint}
            onSubmit={handleResultSubmit}
            onUndo={handleUndoResult}
            onEdit={handleEditResult}
            onChanged={loadResults}
          />
        )}

        {tab === "text" && (
          <TextsTab
            ocrFiles={ocrFiles}
            setOcrFiles={setOcrFiles}
            ocrLoading={ocrLoading}
            ocrProgress={ocrProgress}
            ocrResult={ocrResult}
            setOcrResult={setOcrResult}
            pubVariant={pubVariant}
            setPubVariant={setPubVariant}
            pubCategoryId={pubCategoryId}
            setPubCategoryId={setPubCategoryId}
            publishing={publishing}
            editorZoom={editorZoom}
            setEditorZoom={setEditorZoom}
            ocrTexts={ocrTexts}
            categories={categories}
            onExtract={handleExtract}
            onUpdateText={handleUpdateText}
            onViewText={handleViewText}
            onDeleteText={handleDeleteText}
            onPublish={handlePublish}
          />
        )}

        {tab === "telegram" && (
          <TelegramTab
            telegramConfigured={telegramConfigured}
            telegramInfo={telegramInfo}
            blastLimit={blastLimit}
            setBlastLimit={setBlastLimit}
            blasting={blasting}
            onBlast={handleBlast}
            sched={sched}
            setSched={setSched}
            onSaveSchedule={handleSaveSchedule}
            weeklyCardUrl={weeklyCardUrl}
            onWeeklyPreview={handleWeeklyPreview}
            onWeeklySend={handleWeeklySend}
            statusCardUrl={statusCardUrl}
            statusDate={statusDate}
            setStatusDate={setStatusDate}
            onStatusPreview={handleStatusPreview}
            onStatusSend={handleStatusSend}
            onAlertTest={handleAlertTest}
          />
        )}
      </div>
    </div>
  );
}
